/**
 * *Rename as a Set* (`equipment-page-bulk-actions`, R2; frame 07, `Equipment Page.dc.html:393–408`):
 * one base name, a preview of every selected row's old → new name in the order shown, and
 * *Rename {n}*. The targets and the write order are `planSetRename`'s; the writes are
 * `useEquipmentBulkWrites.renameSet`'s.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Field, Icon, Input } from '@repo/design-system';
import { fontMono, ink, space, typeRole, weight } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';
import {
  buildTwinNameIndex,
  planSetRename,
  registerRowName,
  renameBase,
  type SetRenamePlan,
} from '@repo/front-end-utils';
import type { TwinAssetResponse } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import { BulkPopoverFrame } from './BulkPopoverFrame';
import { COUNT_ANNOUNCE_DELAY_MS } from './EquipmentStage';
import { nameAnswer, nameIndexWithout } from './equipmentRowEdit';

export const TYPE_A_NAME = 'Type a name';

/** The preview scrolls inside the popover past this many rows (R2). */
const PREVIEW_ROWS = 8;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  .suffix {
    font-family: ${fontMono};
    font-size: ${typeRole.captionCompact.fontSize};
    line-height: ${typeRole.captionCompact.lineHeight};
    font-weight: ${weight.medium};
    color: ${ink[3]};
    white-space: nowrap;
  }

  .preview {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: ${space[1]} ${space[2]};
    align-items: center;
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: calc(${PREVIEW_ROWS} * (${typeRole.caption1.lineHeight} + ${space[1]}));
    overflow-y: auto;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
  }
  .preview li {
    display: contents;
  }
  .old {
    color: ${ink[3]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .arrow {
    color: ${ink[4]};
  }
  .new {
    font-weight: ${weight.semibold};
    color: ${ink[1]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .note {
    margin: 0;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${ink[3]};
  }
`;

export interface EquipmentRenameSetPopoverProps {
  id: string;
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  /** The selected rows in the order shown (never the tick order). */
  rows: readonly TwinAssetResponse[];
  /** The register-joined layout and the register, for the twin's names. */
  layout: FloorPlannerLayout | null | undefined;
  registerRows: readonly TwinAssetResponse[];
  busy: boolean;
  onRename: (rows: { id: number; name: string }[], plan: SetRenamePlan) => void;
}

export const EquipmentRenameSetPopover: React.FC<EquipmentRenameSetPopoverProps> = ({
  id,
  open,
  onClose,
  anchorRef,
  rows,
  layout,
  registerRows,
  busy,
  onRename,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const n = rows.length;
  const named = useMemo(
    () => rows.map(row => ({ id: row.id, name: registerRowName(row) })),
    [rows],
  );
  const [base, setBase] = useState('');
  const [touched, setTouched] = useState(false);

  // Each opening starts from the first shown row's family, with its text selected (R2).
  useEffect(() => {
    if (!open) return;
    setBase(renameBase(named[0]?.name ?? ''));
    setTouched(false);
    const timer = window.setTimeout(() => inputRef.current?.select(), 0);
    return () => window.clearTimeout(timer);
    // Only on opening: the rows re-read under an open popover must not overwrite what is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const index = useMemo(
    () => (open ? buildTwinNameIndex(layout, registerRows) : null),
    [open, layout, registerRows],
  );
  const plan = useMemo<SetRenamePlan | null>(() => {
    if (!open || !index) return null;
    // One row is named exactly as the cell would name it, from the text as typed (a typed
    // *Treadmill-3* stays *Treadmill-3*); its holder is never renamed (R2, R3).
    const single =
      rows.length === 1
        ? nameAnswer(base, rows[0], nameIndexWithout(layout, registerRows, rows[0].id)).name ||
          undefined
        : undefined;
    return planSetRename({ rows: named, base, index, single });
  }, [open, index, rows, named, base, layout, registerRows]);

  const empty = plan?.empty ?? true;
  const changing = plan ? named.length - new Set(plan.unchanged).size : 0;

  // The count of names that will change, said politely once the typing pauses.
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(
      () =>
        setAnnounced(empty ? '' : `${changing} ${changing === 1 ? 'name' : 'names'} will change`),
      COUNT_ANNOUNCE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [open, empty, changing]);

  const submit = () => {
    if (!plan || plan.empty) {
      setTouched(true);
      return;
    }
    onRename(named, plan);
  };

  return (
    <BulkPopoverFrame
      id={id}
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      glyph="i-cursor"
      title={`Rename ${n} as a set`}
      primaryLabel={`Rename ${n}`}
      onPrimary={submit}
      primaryUnavailable={empty ? TYPE_A_NAME : null}
      busy={busy}
      initialFocusRef={inputRef}
    >
      <Body>
        <Field label="Base name" error={empty && touched ? TYPE_A_NAME : undefined}>
          {wiring => (
            <Input
              {...wiring}
              ref={inputRef}
              size="sm"
              value={base}
              invalid={empty && touched}
              trail={<span className="suffix">{n > 1 ? `-1 … -${n}` : null}</span>}
              onChange={event => {
                setBase(event.target.value);
                setTouched(true);
              }}
              onKeyDown={event => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                submit();
              }}
            />
          )}
        </Field>
        <ul className="preview" aria-label="What changes">
          {plan && !plan.empty
            ? named.map(row => {
                const to = plan.targets.get(row.id) ?? row.name;
                return (
                  <li key={row.id}>
                    <span className="old">{row.name}</span>
                    <span className="arrow" aria-hidden="true">
                      <Icon name="arrow-right" size="xs" />
                    </span>
                    <span style={visuallyHidden}> becomes </span>
                    <span className="new">{to}</span>
                  </li>
                );
              })
            : null}
        </ul>
        <p className="note">Numbered in the order shown. Sort first to choose the order.</p>
        <span role="status" aria-live="polite" style={visuallyHidden}>
          {announced}
        </span>
      </Body>
    </BulkPopoverFrame>
  );
};

export default EquipmentRenameSetPopover;
